import React, { useContext, useState, useMemo } from "react";
import { useQuery } from "react-apollo-hooks";
import { observer } from "mobx-react-lite";
import { StoreContext } from "../../store";
import gql from "graphql-tag";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import GetAppIcon from "@material-ui/icons/GetApp";
import { Button } from "@material-ui/core";

const query = gql`
query getPayments($email: String, $date_limit: date) {
    payment_plan(
      where: {
        enrollmentByEnrollment: { email: { _eq: $email } }
        deleted_at: { _is_null: true }
      }
      order_by: { id: desc }
    ) {
      id
      payments(
        order_by: { due_date: asc }
        where: { deleted_at: { _is_null: true } }
      ) {
      payment_id
      due_date
      status
      value
      link
      updated_at
      description
      disabled
    }
    enrollmentByEnrollment {
      course
    }
  }
}
`;

const NativePaymentTable = ({ planData }) => {
  const [state, setState] = useState({
    currentPage: 0,
    rowsPerPage: 5,
    orderBy: "due_date",
    order: "asc",
    searchTerm: "",
  });

  const handleChangePage = (newPage, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setState({ ...state, currentPage: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    setState({
      ...state,
      rowsPerPage: parseInt(event.target.value, 10),
      currentPage: 0,
    });
  };

  const handleRequestSort = (property) => {
    const isAsc = state.orderBy === property && state.order === "asc";
    setState({
      ...state,
      order: isAsc ? "desc" : "asc",
      orderBy: property,
    });
  };

  const handleSearch = (event) => {
    setState({ ...state, searchTerm: event.target.value, currentPage: 0 });
  };

  const columns = [
    { id: "due_date", label: "Vencimento", sortable: true },
    { id: "status", label: "Status", sortable: true },
    { id: "value", label: "Valor (R$)", sortable: true },
    { id: "description", label: "Descrição", sortable: true },
    { id: "link", label: "Link", sortable: false },
    { id: "updated_at", label: "Última Atualização", sortable: true },
  ];

  const getStatusStyle = (status) => {
    let style = {
          borderRadius: "5px",
      padding: "5px",
      textAlign: "center",
      display: "inline-block",
      minWidth: "80px",
    };

    switch (status) {
      case "pago":
        style.background = "lightgreen";
        break;
      case "ATRASO":
        style.background = "orange";
          break;
      case "aberto":
        style.background = "yellow";
          break;
      default:
          break;
    }

    return style;
  };

  const {
    filteredData,
    paginatedData,
    totalPages,
    emptyRows,
    start,
  } = useMemo(() => {
    const filtered = planData.dataView.filter((payment) => {
      const searchLower = state.searchTerm.toLowerCase();

      const matchDescription =
        payment.description &&
        payment.description.toLowerCase().includes(searchLower);

      const matchStatus =
        payment.status && payment.status.toLowerCase().includes(searchLower);

      const matchValue =
        payment.value && payment.value.toString().includes(searchLower);

      return matchDescription || matchStatus || matchValue;
    });

    const sorted = [...filtered].sort((a, b) => {
      const isAsc = state.order === "asc";

      if (!a[state.orderBy] && !b[state.orderBy]) return 0;
      if (!a[state.orderBy]) return 1;
      if (!b[state.orderBy]) return -1;

      if (state.orderBy === "due_date") {
        const parseDate = (dateStr) => {
          const parts = dateStr.split("/");
          return new Date(parts[2], parts[1] - 1, parts[0]); // Ano, Mês (0-11), Dia
        };

        const dateA = parseDate(a[state.orderBy]);
        const dateB = parseDate(b[state.orderBy]);

        return isAsc ? dateA - dateB : dateB - dateA;
      }

      return isAsc
        ? String(a[state.orderBy]).localeCompare(String(b[state.orderBy]))
        : String(b[state.orderBy]).localeCompare(String(a[state.orderBy]));
    });

    const start = state.currentPage * state.rowsPerPage;
    const paginated = sorted.slice(start, start + state.rowsPerPage);
    const total = Math.ceil(sorted.length / state.rowsPerPage);
    const empty =
      state.rowsPerPage - Math.min(state.rowsPerPage, sorted.length - start);

    return {
      filteredData: filtered,
      paginatedData: paginated,
      totalPages: total,
      emptyRows: empty,
      start,
    };
  }, [
    planData.dataView,
    state.searchTerm,
    state.orderBy,
    state.order,
    state.currentPage,
    state.rowsPerPage,
  ]);

  const exportToCSV = () => {
    const headers = [
      "Vencimento",
      "Status",
      "Valor (R$)",
      "Descrição",
      "Link",
      "Última Atualização"
    ];

    const csvData = filteredData.map((payment) => [
      payment.due_date,
      payment.status,
      payment.value,
      payment.description || "",
      payment.link || "",
      payment.updated_at || ""
    ]);

    const csvContent = [
      headers.join(";"),
      ...csvData.map((row) => row.join(";")),
    ].join("\n");

    const blob = new Blob(["\ufeff" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `pagamentos_${planData.name.replace(/\s+/g, '_')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div style={{ margin: "20px 0" }}>
      <div
        style={{
          backgroundColor: "#f5f5f5",
          padding: "15px",
          borderRadius: "8px 8px 0 0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "2px solid #3f51b5",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          <h3 style={{ margin: 0 }}>Pagamentos - {planData.name}</h3>
          <Button
            variant="contained"
            color="primary"
            startIcon={<GetAppIcon />}
            onClick={exportToCSV}
            size="small"
          >
            Exportar CSV
          </Button>
        </div>
        <input
          type="text"
          placeholder="Buscar por descrição, status ou valor..."
          value={state.searchTerm}
          onChange={handleSearch}
          style={{
            padding: "8px",
            borderRadius: "4px",
            border: "1px solid #ccc",
            width: "250px",
          }}
        />
      </div>

      <div
        style={{
          overflowX: "auto",
          boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
          borderRadius: "0 0 4px 4px",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            border: "1px solid #ddd",
          }}
        >
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.id}
                  style={{
                    border: "1px solid #ddd",
                    padding: "10px",
                    backgroundColor: "#f2f2f2",
                    cursor: column.sortable ? "pointer" : "default",
                  }}
                  onClick={() =>
                    column.sortable && handleRequestSort(column.id)
                  }
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {column.label}
                    {column.sortable && (
                      <span
                        style={{ display: "inline-block", marginLeft: "5px" }}
                      >
                        {state.orderBy === column.id ? (
                          state.order === "asc" ? (
                            <ArrowUpwardIcon fontSize="small" />
                          ) : (
                            <ArrowDownwardIcon fontSize="small" />
                          )
                        ) : null}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((payment, index) => (
              <tr
                key={index}
                style={{
                  backgroundColor: index % 2 === 0 ? "#f9f9f9" : "white",
                  cursor: "pointer",
                  transition: "background-color 0.2s",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = "#f0f0f0";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor =
                    index % 2 === 0 ? "#f9f9f9" : "white";
                }}
              >
                <td
                  style={{ border: "1px solid #ddd", padding: "10px" }}
                  onClick={() =>
                    payment.link && window.open(payment.link, "_blank")
                  }
                >
                  {payment.due_date}
                </td>
                <td
                  style={{ border: "1px solid #ddd", padding: "10px" }}
                  onClick={() =>
                    payment.link && window.open(payment.link, "_blank")
                  }
                >
                  <div style={getStatusStyle(payment.status)}>
                    {payment.status}
                  </div>
                </td>
                <td
                  style={{ border: "1px solid #ddd", padding: "10px" }}
                  onClick={() =>
                    payment.link && window.open(payment.link, "_blank")
                  }
                >
                  {payment.value}
                </td>
                <td
                  style={{ border: "1px solid #ddd", padding: "10px" }}
                  onClick={() =>
                    payment.link && window.open(payment.link, "_blank")
                  }
                >
                  {payment.description}
                </td>
                <td
                  style={{
                    border: "1px solid #ddd",
                    padding: "10px",
                    cursor: "pointer",
                    color: payment.link ? "blue" : "inherit",
                    textDecoration: payment.link ? "underline" : "none",
                  }}
                  onClick={() =>
                    payment.link && window.open(payment.link, "_blank")
                  }
                >
                  {payment.link ? "Visualizar" : ""}
                </td>
                <td
                  style={{ border: "1px solid #ddd", padding: "10px" }}
                  onClick={() =>
                    payment.link && window.open(payment.link, "_blank")
                  }
                >
                  {payment.updated_at}
                </td>
              </tr>
            ))}
            {emptyRows > 0 && (
              <tr style={{ height: 53 * emptyRows }}>
                <td
                  colSpan={columns.length}
                  style={{ border: "1px solid #ddd" }}
                />
              </tr>
            )}
            {filteredData.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{
                    textAlign: "center",
                    padding: "20px",
                    border: "1px solid #ddd",
                  }}
                >
                  Nenhum pagamento encontrado
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Paginação */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "20px",
        }}
      >
        <div>
          <span>Linhas por página: </span>
          <select
            value={state.rowsPerPage}
            onChange={handleChangeRowsPerPage}
            style={{ marginLeft: "5px", padding: "5px" }}
          >
            {[5, 10, 25, 50, 100].map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div>
          <span>
            {filteredData.length > 0
              ? `Mostrando ${start + 1}-${Math.min(
                  start + state.rowsPerPage,
                  filteredData.length
                )}`
              : "Nenhum item"}
          </span>
        </div>
        <div>
          <button
            type="button"
            onClick={(e) => handleChangePage(state.currentPage - 1, e)}
            disabled={state.currentPage === 0}
            style={{
              padding: "5px 10px",
              margin: "0 5px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              backgroundColor: state.currentPage === 0 ? "#f0f0f0" : "white",
              cursor: state.currentPage === 0 ? "not-allowed" : "pointer",
            }}
          >
            Anterior
          </button>
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              type="button"
              onClick={(e) => handleChangePage(i, e)}
              disabled={i === state.currentPage}
              style={{
                padding: "5px 10px",
                margin: "0 5px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                backgroundColor: i === state.currentPage ? "#007bff" : "white",
                color: i === state.currentPage ? "white" : "black",
                cursor: i === state.currentPage ? "default" : "pointer",
              }}
            >
              {i + 1}
            </button>
          )).slice(
            Math.max(0, state.currentPage - 2),
            Math.min(totalPages, state.currentPage + 3)
          )}
          <button
            type="button"
            onClick={(e) => handleChangePage(state.currentPage + 1, e)}
            disabled={state.currentPage >= totalPages - 1}
            style={{
              padding: "5px 10px",
              margin: "0 5px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              backgroundColor:
                state.currentPage >= totalPages - 1 ? "#f0f0f0" : "white",
              cursor:
                state.currentPage >= totalPages - 1 ? "not-allowed" : "pointer",
            }}
          >
            Próximo
          </button>
        </div>
      </div>
    </div>
  );
};

const PaymentsStudent = observer((props) => {
  const store = useContext(StoreContext);
  let variables = { email: store.email };

  var { data, error, loading } = useQuery(query, { variables });

  if (loading)
    return (
      <div
        style={{
          padding: "20px",
          textAlign: "center",
          backgroundColor: "#f5f5f5",
          borderRadius: "8px",
        }}
      >
        <p>Carregando do banco de dados... </p>
      </div>
    );

  if (error)
    return (
      <div
        style={{
          padding: "20px",
          textAlign: "center",
          backgroundColor: "#ffebee",
          borderRadius: "8px",
          color: "#f44336",
        }}
      >
        <p>ERROR (Payments.jsx): {error.message}</p>
      </div>
    );

  data = data["payment_plan"];
  data = JSON.parse(JSON.stringify(data)); //clone data to preserve original object

  var plans = data.map((plan) => {
    var dataView = [];
    var course = plan.enrollmentByEnrollment.course;

    plan.payments.forEach((payment) => {
      if (payment.disabled || payment.status === "expired") {
        return;
      }

      let date = new Date(payment.due_date + 'T00:00:00-03:00');
      let formattedDate = date.getDate().toString().padStart(2, '0') + '/' +
                         (date.getMonth() + 1).toString().padStart(2, '0') + '/' +
                         date.getFullYear();

      if (payment.status === "pending") {
        if (payment.due_date >= new Date().toISOString().split("T")[0]) {
          payment.status = "aberto";
          payment.due_date = formattedDate;
        } else {
          payment.status = "ATRASO";
          payment.due_date = formattedDate;
        }
        dataView.push(payment);
      } else if (payment.status === "paid") {
        payment.status = "pago";
        payment.due_date = formattedDate;
        dataView.push(payment);
      }

      if (payment.updated_at) {
        let att = new Date(payment.updated_at);
        payment.updated_at = att.getDate().toString().padStart(2, '0') + '/' +
                            (att.getMonth() + 1).toString().padStart(2, '0') + '/' +
                            att.getFullYear() + ' ' +
                            att.getHours().toString().padStart(2, '0') + ':' +
                            att.getMinutes().toString().padStart(2, '0') + ':' +
                            att.getSeconds().toString().padStart(2, '0');
      }
    });

    return {
      name: course,
      dataView,
    };
  });

  const findNextPayment = () => {
    let allPayments = [];

    plans.forEach((plan) => {
      const pendingPayments = plan.dataView.filter(
        (payment) => payment.status === "aberto" || payment.status === "ATRASO"
      );
      allPayments = [
        ...allPayments,
        ...pendingPayments.map((p) => ({ ...p, course: plan.name })),
      ];
    });

    if (allPayments.length === 0) return null;

    allPayments.sort((a, b) => {
      const parseDate = (dateStr) => {
        const parts = dateStr.split("/");
        return new Date(parts[2], parts[1] - 1, parts[0]); // Ano, Mês (0-11), Dia
      };

      const dateA = parseDate(a.due_date);
      const dateB = parseDate(b.due_date);

      return dateA - dateB;
    });

    return allPayments[0];
  };

  const nextPayment = findNextPayment();

  return (
    <div style={{ padding: "20px" }}>
      <h2
        style={{
          color: "#333",
          borderBottom: "2px solid #3f51b5",
          paddingBottom: "10px",
          marginBottom: "20px",
        }}
      >
        Meus Pagamentos
      </h2>

      {/* Aviso da próxima fatura a ser paga */}
      {nextPayment && (
        <div
          style={{
            backgroundColor:
              nextPayment.status === "ATRASO" ? "#fff3cd" : "#e8f5e9",
            border: `1px solid ${
              nextPayment.status === "ATRASO" ? "#ffeeba" : "#c8e6c9"
            }`,
            borderLeft: `5px solid ${
              nextPayment.status === "ATRASO" ? "#ffc107" : "#4caf50"
            }`,
            borderRadius: "4px",
            padding: "15px 20px",
            marginBottom: "25px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
          }}
        >
          <div>
            <h3
              style={{
                margin: "0 0 10px 0",
                color: nextPayment.status === "ATRASO" ? "#856404" : "#2e7d32",
              }}
            >
              {nextPayment.status === "ATRASO"
                ? "⚠️ Fatura em atraso"
                : "📆 Próxima fatura a vencer"}
            </h3>
            <p style={{ margin: "5px 0", fontSize: "16px" }}>
              <strong>Vencimento:</strong> {nextPayment.due_date}
            </p>
            <p style={{ margin: "5px 0", fontSize: "16px" }}>
              <strong>Valor:</strong> R$ {nextPayment.value}
            </p>
            <p style={{ margin: "5px 0", fontSize: "16px" }}>
              <strong>Descrição:</strong> {nextPayment.description}
            </p>
            <p style={{ margin: "5px 0", fontWeight: "bold" }}>
              Pode haver mais de uma fatura pendente. Verifique todos os
              pagamentos na tabela abaixo.
            </p>
          </div>
          {nextPayment.link && (
            <button
              onClick={() => window.open(nextPayment.link, "_blank")}
              style={{
                backgroundColor:
                  nextPayment.status === "ATRASO" ? "#dc3545" : "#007bff",
                color: "white",
                border: "none",
                borderRadius: "4px",
                padding: "10px 15px",
                fontSize: "16px",
                cursor: "pointer",
                fontWeight: "bold",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                transition: "all 0.2s ease",
              }}
            >
              {nextPayment.status === "ATRASO"
                ? "Pagar Agora!"
                : "Visualizar Fatura"}
            </button>
          )}
        </div>
      )}

      {plans.length > 0 ? (
        plans.map((plan, index) => (
          <NativePaymentTable key={index} planData={plan} />
        ))
      ) : (
        <div
          style={{
            padding: "20px",
            textAlign: "center",
            backgroundColor: "#fff3e0",
            borderRadius: "8px",
            marginTop: "20px",
          }}
        >
          <p>Nenhum plano de pagamento encontrado.</p>
        </div>
      )}
      </div>
  );
});
  
export default PaymentsStudent;
